import { useCallback, useEffect, useState, useMemo } from 'react';
import { useFetcher, useLoaderData, Link, useLocation } from 'react-router';
import { useAtomValue } from 'jotai';
import { categoryToggleAtom } from '@rocco/states/categoryToggleAtom';
import type { loader as productLoader } from '~/routes/_app.products.$';
import type { ProductTileViewModel } from '@rocco/ui/tile-product/view-models/product';
import { Button } from '@rocco/components/button';
import { DiscoverHeader } from '@ui-rocco/pages/category-page/components/DiscoverHeader';
import { CategoryPills, type CategoryPillItem } from './CategoryPills';
import { EditorsChoiceProductTile } from './EditorsChoiceProductTile';
import { EDITORS_CHOICE_PRODUCTS_ACTION } from '~/routes/actions.editors-choice-products';

const INITIAL_VISIBLE_ROOT = 8;
const EXPANDED_VISIBLE_ROOT = 12;
const VISIBLE_CATEGORY = 4;

/** Discriminated union matching the action route response shapes. */
type EditorsChoiceData =
    | { mode: 'single'; products: ProductTileViewModel[] }
    | {
          mode: 'batch';
          productsByCategory: Record<string, ProductTileViewModel[]>;
      };

/**
 * Editors' Choice / Featured section.
 * Desktop only - hidden on mobile.
 *
 * Renders differently based on route and category toggle:
 * - Root /products + Interior toggle: "Featured this week in Curated Collection" with 8→12 expandable products
 * - Root /products + Building toggle: "Featured this week in The Build" with 8→12 expandable products
 * - Category pages: "Editors' Choice" with category pills and 4 products (pre-fetched for all categories)
 */
export const EditorsChoice = () => {
    const { productSection } = useLoaderData<typeof productLoader>();
    const fetcher = useFetcher<EditorsChoiceData>();
    const location = useLocation();
    const activeToggle = useAtomValue(categoryToggleAtom);

    // Detect page context
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const isProductsRoot =
        pathSegments.length === 1 && pathSegments[0] === 'products';

    // State for root page expansion
    const [expanded, setExpanded] = useState(false);

    // State for tracking which pill the user clicked (category pages only)
    const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(
        null,
    );

    // Get all categories for category pages (not used on root /products page)
    const categories = useMemo(() => {
        if (isProductsRoot) {
            return [];
        }
        return (
            productSection.categories?.map((cat) => ({
                id: cat.id,
                title: cat.title,
                link: cat.link,
                itemsCount: cat.itemsCount ?? 0,
            })) ?? []
        );
    }, [isProductsRoot, productSection]);

    // Reset selected pill when navigating to a different category page
    useEffect(() => {
        setSelectedCategoryId(null);
    }, [categories]);

    // Determine newsletter source based on category toggle (root page only)
    const isBuilding = isProductsRoot && activeToggle === 'building';

    // Fetch products: batch for category pages, single for root page
    useEffect(() => {
        if (isProductsRoot) {
            // Root page: fetch newsletter products based on toggle
            const action = isBuilding
                ? `${EDITORS_CHOICE_PRODUCTS_ACTION}?source=build`
                : EDITORS_CHOICE_PRODUCTS_ACTION;
            fetcher.load(action);
        } else if (categories.length > 0) {
            // Category page: batch-fetch products for ALL subcategories at once
            const params = new URLSearchParams();
            categories.forEach((c) => params.append('categoryLink', c.link));
            fetcher.load(`${EDITORS_CHOICE_PRODUCTS_ACTION}?${params}`);
        }
        // fetcher from useFetcher() is a stable reference — intentionally omitted
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isProductsRoot, categories, isBuilding]);

    // Reset expanded state when toggle changes
    useEffect(() => {
        setExpanded(false);
    }, [activeToggle]);

    // Handler for expanding products (root page only) - must be before early return
    const handleViewMore = useCallback(() => {
        setExpanded(true);
    }, []);

    // Handler for pill clicks — instant, no new fetch needed
    const handleSelectCategory = useCallback((cat: CategoryPillItem) => {
        setSelectedCategoryId(cat.id);
    }, []);

    // Extract productsByCategory map from batch response
    const productsByCategory = useMemo(() => {
        if (fetcher.data?.mode === 'batch')
            return fetcher.data.productsByCategory;
        return null;
    }, [fetcher.data]);

    // Filter out categories that have 0 products (only after batch data arrives)
    const filteredCategories = useMemo(() => {
        if (!productsByCategory) return categories;
        return categories.filter(
            (cat) => (productsByCategory[cat.link]?.length ?? 0) > 0,
        );
    }, [categories, productsByCategory]);

    // Derive selected category from filtered list
    const selectedCategory = useMemo((): CategoryPillItem | null => {
        if (isProductsRoot) return null;
        const found = filteredCategories.find(
            (c) => c.id === selectedCategoryId,
        );
        return found ?? filteredCategories[0] ?? null;
    }, [isProductsRoot, filteredCategories, selectedCategoryId]);

    // Get products for the current view
    const products = useMemo((): ProductTileViewModel[] => {
        if (!fetcher.data) return [];
        if (fetcher.data.mode === 'batch') {
            return selectedCategory
                ? (fetcher.data.productsByCategory[selectedCategory.link] ?? [])
                : [];
        }
        return fetcher.data.products ?? [];
    }, [fetcher.data, selectedCategory]);

    const isLoading =
        fetcher.state === 'loading' ||
        (fetcher.state === 'idle' &&
            !fetcher.data &&
            (isProductsRoot || categories.length > 0));

    // Hide entire section if all categories are empty (after loading completes)
    if (!isProductsRoot && filteredCategories.length === 0 && !isLoading) {
        return null;
    }

    const visibleProducts = products.slice(
        0,
        isProductsRoot
            ? expanded
                ? EXPANDED_VISIBLE_ROOT
                : INITIAL_VISIBLE_ROOT
            : VISIBLE_CATEGORY,
    );
    const hasNoProducts = !isLoading && products.length === 0;
    const canExpand =
        isProductsRoot && !expanded && products.length > INITIAL_VISIBLE_ROOT;

    // Determine skeleton count based on mode
    const skeletonCount = isProductsRoot
        ? INITIAL_VISIBLE_ROOT
        : VISIBLE_CATEGORY;

    return (
        <section
            className="mb-20 hidden md:mb-8 md:block"
            aria-busy={isLoading}
        >
            {/* Header - different for root vs category pages */}
            {isProductsRoot ? (
                // Featured section header (root /products page)
                <div className="flex flex-col gap-4 px-6 md:px-12">
                    <p className="text-[0.9375rem] leading-[1.22] font-medium tracking-[-0.75px] text-[#9a9a9a]">
                        ArchiPro Digital Publication
                    </p>
                    <h2 className="text-[4rem] leading-none font-medium tracking-[-3px] text-[#141414] lg:text-[5rem] xl:text-[6.25rem]">
                        Featured this week in
                        <br />
                        <span className="text-[#a3a3a3]">
                            {isBuilding ? 'The Build' : 'Curated Collection'}
                        </span>
                    </h2>
                </div>
            ) : isLoading ? (
                // Skeleton placeholder for pills while batch is loading
                <div className="flex flex-col gap-3 px-6 md:px-12">
                    <div className="h-16 w-80 animate-pulse rounded bg-gray-200" />
                    <div className="flex gap-3">
                        {[...Array(Math.min(categories.length, 6))].map(
                            (_, i) => (
                                <div
                                    key={i}
                                    className="h-10.5 w-24 animate-pulse rounded-full bg-gray-200"
                                />
                            ),
                        )}
                    </div>
                </div>
            ) : (
                // Editors' Choice header with pills (category pages, data loaded)
                <div className="flex flex-col gap-3 px-6 md:px-12">
                    <h2 className="text-5xl leading-[130%] font-medium tracking-[-0.1rem] text-[#141414] md:text-[5rem]">
                        Editors' Choice
                    </h2>
                    <CategoryPills
                        categories={filteredCategories}
                        selectedCategory={selectedCategory}
                        onSelectCategory={handleSelectCategory}
                    />
                </div>
            )}
            {/* DiscoverHeader - only for category pages when loaded */}
            {!isProductsRoot && !isLoading && selectedCategory && (
                <DiscoverHeader
                    count={selectedCategory.itemsCount}
                    categoryTitle={selectedCategory.title}
                />
            )}
            {/* Product Grid */}
            <div
                className={
                    isProductsRoot
                        ? 'px-6 pt-[4.375rem] md:px-12'
                        : 'px-6 md:px-12'
                }
            >
                {isLoading ? (
                    <div className="grid grid-cols-4 gap-x-5 gap-y-[3.125rem]">
                        {[...Array(skeletonCount)].map((_, i) => (
                            <div key={i} className="flex flex-col gap-3.75">
                                <div className="aspect-440/582 animate-pulse rounded-lg bg-gray-200" />
                                <div className="flex flex-col gap-[2px]">
                                    <div className="h-[1.3rem] w-3/4 animate-pulse rounded bg-gray-200" />
                                    <div className="h-[1.22rem] w-1/2 animate-pulse rounded bg-gray-200" />
                                </div>
                            </div>
                        ))}
                    </div>
                ) : hasNoProducts ? (
                    <div className="py-12 text-center">
                        <p className="text-base text-gray-600">
                            {isProductsRoot
                                ? 'No featured products available at this time.'
                                : 'No products found in this category. Please select another category.'}
                        </p>
                    </div>
                ) : (
                    <div className="grid grid-cols-4 gap-x-5 gap-y-[3.125rem]">
                        {visibleProducts.map((product) => (
                            <EditorsChoiceProductTile
                                key={product.id}
                                product={product}
                            />
                        ))}
                    </div>
                )}
            </div>
            {/* View More - different behavior for root vs category pages */}
            {isProductsRoot
                ? // Root page: Expand button. When no button, only pb remains (matching gap from button-bottom to next section)
                  (canExpand || isLoading || expanded) && (
                      <div
                          className={`flex flex-col items-center pb-[5.625rem] ${canExpand ? 'pt-[6.25rem]' : ''}`}
                      >
                          {canExpand && (
                              <Button
                                  onClick={handleViewMore}
                                  color="dark"
                                  size="default"
                                  shape="default"
                                  className="h-10.5 min-w-[10.625rem] border border-[#141414] bg-[#141414] text-base font-medium text-[#f2f2f2] transition-colors duration-200 hover:bg-white hover:text-[#141414]"
                              >
                                  View More
                              </Button>
                          )}
                      </div>
                  )
                : // Category page: Navigation link
                  selectedCategory && (
                      <div className="flex flex-col items-center pt-[3.125rem] pb-[5.625rem]">
                          <Button
                              asChild
                              color="dark"
                              size="default"
                              shape="default"
                              className="h-10.5 min-w-[10.625rem] border border-[#141414] bg-[#141414] text-base font-medium text-[#f2f2f2] transition-colors duration-200 hover:bg-white hover:text-[#141414]"
                          >
                              <Link to={`${selectedCategory.link}?browse=true`}>
                                  View More
                              </Link>
                          </Button>
                      </div>
                  )}
        </section>
    );
};
